import { supabase } from "@/integrations/supabase/client";
import type { Article, Category } from "@/data/articles";
import { normalizeCategory } from "@/data/articles";

const PLACEHOLDER_PATTERNS = [
  /awaiting/i,
  /placeholder/i,
  /to be determined/i,
  /\btbd\b/i,
  /lorem ipsum/i,
  /sample article/i,
  /test article/i,
  /will be generated/i,
  /once the user provides/i,
  /awaiting specific content details/i,
  /a brief summary of the editorial content/i,
  /this is the main content of the editorial/i,
  /it will be a long-form piece/i,
  /key takeaway \d/i,
  /source \d+ name/i,
  /insert .* here/i,
];

const BANNED_TITLE_PATTERNS = [
  /\bIEA\b.*\b(forecast|outlook|report|slash|cut|rais)/i,
  /\bOPEC\+?\b.*\b(agree|agrees|agreed|extend|extends|extended|production cuts?|output cuts?)\b/i,
  /\bEIA\b.*\b(forecast|outlook|report)/i,
  /oil prices?\s+(surge|rise|fall|drop|climb|dip|slip)/i,
  /brent\s+(crude\s+)?(surge|rise|breach|break|fall|drop|climb)/i,
  /\bmarket uncertainty\b/i,
  /\binflation fears\b/i,
  /\beconomic headwinds?\b/i,
];

interface DBArticleRow {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  author: string;
  publication_date: string;
  category: string;
  tags: string[];
  image_url: string;
  image_alt: string;
  is_opinion: boolean;
  is_feature: boolean;
  is_trending: boolean;
  is_published: boolean;
  is_hero: boolean;
  is_latest: boolean;
  hero_position: number | null;
  read_time: number;
  key_points: string[];
  sources: any;
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function hasPlaceholderSources(sources: unknown) {
  if (!Array.isArray(sources)) return false;

  return sources.some((source) => {
    const name = typeof source?.name === "string" ? source.name : "";
    const url = typeof source?.url === "string" ? source.url : "";

    return (
      PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(name)) ||
      /example\.com/i.test(url) ||
      (!!url && !/^https?:\/\//i.test(url))
    );
  });
}

function isValidDBArticle(row: DBArticleRow) {
  const plainContent = stripHtml(row.content ?? "");
  const combinedText = [row.title, row.summary, plainContent, ...(row.key_points ?? [])].join("\n");
  const hasPlaceholderText = PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(combinedText));
  const hasBannedTitle = BANNED_TITLE_PATTERNS.some((pattern) => pattern.test(row.title ?? ""));
  const wordCount = plainContent ? plainContent.split(/\s+/).length : 0;

  return !hasPlaceholderText && !hasBannedTitle && !hasPlaceholderSources(row.sources) && wordCount >= 100;
}

function rowToArticle(a: DBArticleRow): Article & { isHero: boolean; isLatest: boolean; heroPosition: number | null } {
  return {
    id: a.id,
    slug: a.slug,
    title: a.title,
    summary: a.summary,
    content: a.content,
    author: a.author,
    publicationDate: a.publication_date,
    category: normalizeCategory(a.category),
    tags: a.tags ?? [],
    imageUrl: a.image_url,
    imageAlt: a.image_alt,
    isOpinion: a.is_opinion,
    isFeature: a.is_feature,
    readTime: a.read_time,
    keyPoints: a.key_points ?? [],
    sources: a.sources ?? [],
    isHero: a.is_hero,
    isLatest: a.is_latest,
    heroPosition: a.hero_position ?? null,
  };
}

export type DBArticle = Article & { isHero?: boolean; isLatest?: boolean; isTrending?: boolean; heroPosition?: number | null };

const ARTICLE_SELECT_COLUMNS = "id,slug,title,summary,content,author,publication_date,category,tags,image_url,image_alt,is_opinion,is_feature,is_trending,is_published,is_hero,is_latest,read_time,key_points,sources,hero_position";

// Lightweight columns for listing pages (no content field — saves ~1.5MB)
const ARTICLE_LISTING_COLUMNS = "id,slug,title,summary,author,publication_date,category,tags,image_url,image_alt,is_opinion,is_feature,is_trending,is_published,is_hero,is_latest,read_time,key_points,sources,hero_position";

export async function fetchDBArticles(limit?: number): Promise<DBArticle[]> {
  let query = supabase
    .from("articles")
    .select(ARTICLE_SELECT_COLUMNS)
    .eq("is_published", true)
    .order("publication_date", { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error || !data) return [];
  return (data as unknown as DBArticleRow[])
    .filter(isValidDBArticle)
    .map((r) => ({
      ...rowToArticle(r),
      isTrending: r.is_trending,
    }));
}

/** Fetch articles without the heavy content field — for homepage/category listing */
export async function fetchDBArticlesListing(limit?: number): Promise<DBArticle[]> {
  let query = supabase
    .from("articles")
    .select(ARTICLE_LISTING_COLUMNS)
    .eq("is_published", true)
    .order("publication_date", { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error || !data) return [];
  return (data as unknown as DBArticleRow[])
    .filter((row) => {
      // Simplified validation without content (skip word count check)
      const combinedText = [row.title, row.summary, ...(row.key_points ?? [])].join("\n");
      const hasPlaceholderText = PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(combinedText));
      const hasBannedTitle = BANNED_TITLE_PATTERNS.some((pattern) => pattern.test(row.title ?? ""));
      return !hasPlaceholderText && !hasBannedTitle && !hasPlaceholderSources(row.sources);
    })
    .map((r) => ({
      ...rowToArticle(r),
      isTrending: r.is_trending,
    }));
}

export function mergeArticles(dbArticles: DBArticle[], staticArticles: Article[]): DBArticle[] {
  const dbSlugs = new Set(dbArticles.map((a) => a.slug));
  const staticOnly = staticArticles
    .filter((a) => !dbSlugs.has(a.slug))
    .map((a) => ({ ...a, isHero: false, isLatest: false, isTrending: false, heroPosition: null } as DBArticle));
  const all = [...dbArticles, ...staticOnly];
  // Always sort by publication date descending so newest articles appear first
  return all.sort((a, b) => new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime());
}
